import { FullPageExportShareAbortedError } from './types.js';
import type { ShareOrDownloadOptions } from './types.js';

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * Web Share API for file blobs only. Does **not** fall back to download —
 * caller decides local save vs share (QR label wizard Share CTA — R8 / G4).
 *
 * @throws {FullPageExportShareAbortedError} when the user cancels the share sheet
 * @throws {Error} when `navigator.canShare({ files })` is unavailable / false
 * @throws rethrows non-abort share failures
 */
export async function shareBlobFiles(blob: Blob, options: ShareOrDownloadOptions): Promise<void> {
  const shareFile = new File([blob], options.filename, { type: options.mimeType });
  const canShareFiles =
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [shareFile] });

  if (!canShareFiles) {
    throw new Error('Web Share with files is not available');
  }

  try {
    await navigator.share({
      files: [shareFile],
      title: options.filename,
    });
  } catch (err) {
    if (isAbortError(err)) {
      throw new FullPageExportShareAbortedError(err);
    }
    throw err;
  }
}
